import React, {
  useEffect,
  useRef,
} from 'react';
import 'quill-mention';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-mention/dist/quill.mention.min.css';
import { WysiwygLayoutProps } from 'ui-builder';
import Quill from 'quill';

export interface QuillWysiwygLayoutProps extends WysiwygLayoutProps {
  modules?: Record<string, any>,
  quillRef?: React.Ref<{
    pasteTextOnCursorPosition: (text: string) => void;
  }>,
}

export default function BaseQuillLayout(props: QuillWysiwygLayoutProps) {
  const ref = useRef<ReactQuill>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.editor?.enable(!props.isDisabled);
    }
  }, [ref, props.isDisabled]);

  const maxImageWidth = props.maxImageWidth || 600;

  const modules = {
    toolbar: !props.hideControls ? [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
      ['image'],
    ] : false,
    clipboard: {
      matchVisual: false
    },
    ...props.modules || {},
  };

  const forceOpenTagsMenu = (quill: Quill) => {
    const delta = quill.getContents();
    const source = 'user';

    setTimeout(() => {
      // @ts-ignore
      quill.emitter.emit('text-change', delta, delta, source);
    }, 50);
  };

  const compressImage = (file: File, maxWidth: number, callback: (dataUrl: string) => void) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const scaleFactor = maxWidth / img.width;
        if (scaleFactor < 1) {
          canvas.width = maxWidth;
          canvas.height = img.height * scaleFactor;
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          callback(canvas.toDataURL(file.type));
        } else {
          callback(event.target?.result as string);
        }
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleImagePaste = (event: ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (items) {
      for (const item of Array.from(items)) {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            compressImage(file, maxImageWidth, (dataUrl) => {
              const quill = ref.current?.editor;
              const range = quill?.getSelection();
              quill?.insertEmbed(range?.index || 0, 'image', dataUrl);
            });
          }
        }
      }
    }
  };

  React.useImperativeHandle(props.quillRef, () => ({
    pasteTextOnCursorPosition: (text: string) => {
      const quill = ref.current?.editor;
      if (quill) {
        const cursorPosition = quill.getSelection();
        if (cursorPosition) {
          const indexOfLastHash = quill.getModule('mention').mentionCharPos;

          const lengthToDelete = cursorPosition.index - indexOfLastHash - 1;
          quill.deleteText(indexOfLastHash + 1, lengthToDelete);
          quill.insertText(indexOfLastHash + 1, text);

          // @ts-ignore
          forceOpenTagsMenu(quill);
        }
      }
    }
  }));

  useEffect(() => {
    const quill = ref.current?.editor;
    if (quill && quill.getModule('toolbar')) {
      quill.getModule('toolbar').addHandler('image', () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = () => {
          const file = input.files?.[0];
          if (file) {
            compressImage(file, maxImageWidth, (dataUrl) => {
              const range = quill.getSelection();
              quill.insertEmbed(range?.index || 0, 'image', dataUrl);
            });
          }
        };
      });

      quill.root.addEventListener('paste', handleImagePaste);
    }

    return () => {
      quill?.root.removeEventListener('paste', handleImagePaste);
    };
  }, []);

  return (
    <ReactQuill
      ref={ref}
      theme="snow"
      modules={modules}
      className={props.isDisabled ? 'disabled' : ''}
      value={props.value || ''}
      onChange={(e) => {
        props.onChangeCallback({
          target: {
            value: e,
          },
        });
      }}
    />
  );
}
